import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import { LuBuilding2, LuMapPin, LuPhone, LuMail, LuGlobe, LuUsers } from 'react-icons/lu';
import { getCompanyById } from '../features/lobby/lobbySlice';
import MainLoader from '../components/Structures/MainLoader';
import EngagementsTable from '../components/lobby/EngagementsTable';
import {
  ProfileLayout,
  Panel,
  SectionLabel,
  DetailRow,
  MissingState,
  YearTabs,
} from '../components/lobby/ProfileLayout';
import {
  formatDate,
  formatCompensation,
  hasCompensation,
  lobbyistName,
  StatusBadge,
  yearsOf,
} from '../components/lobby/lobbyFormat';

const Company = () => {
  const { id, state } = useParams();
  const dispatch = useDispatch();
  const { isLoading, isError, company } = useSelector((s) => s.lobby);
  const [year, setYear] = useState(null);
  const [overviewOpen, setOverviewOpen] = useState(false);

  useEffect(() => {
    dispatch(getCompanyById(id));
    setYear(null);
    setOverviewOpen(false);
  }, [dispatch, id]);

  const loaded = company && String(company.CompanyID) === String(id);
  const notFound = company?.message === 'Company not found';
  const lobbyists = useMemo(() => (loaded ? company.lobbyists || [] : []), [loaded, company]);
  const years = useMemo(() => yearsOf(lobbyists), [lobbyists]);
  const activeYear = year ?? years[0] ?? 'all';
  const visible = activeYear === 'all' ? lobbyists : lobbyists.filter((l) => l.Year === activeYear);

  const spend = useMemo(() => {
    let low = 0;
    let high = 0;
    let any = false;
    visible.forEach((l) => {
      const lo = l.Exact ?? l.SalaryLow;
      const hi = l.Exact ?? l.SalaryHigh;
      if (lo != null || hi != null) any = true;
      low += Number(lo) || 0;
      high += Number(hi) || 0;
    });
    return any ? { low, high } : null;
  }, [visible]);

  if (isLoading || (!loaded && !notFound && !isError)) return <MainLoader label="Loading client…" />;
  if (!loaded) {
    return <MissingState title="Client not found" message={`We couldn't find this client in ${state?.toUpperCase()}.`} />;
  }

  const address = [
    company.CompanyAddress1,
    company.CompanyAddress2,
    company.CompanyCity,
    [company.CompanyState, company.CompanyZip].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
  const distinctLobbyists = new Set(visible.map((l) => l.LobbyistID)).size;
  const money = (n) => `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

  const columns = [
    {
      key: 'LobbyistLastName',
      label: 'Lobbyist',
      render: (r) => (
        <Link to={`/${state}/lobbyist/${r.LobbyistID}`} className="font-medium text-info hover:text-info-strong">
          {lobbyistName(r)}
        </Link>
      ),
    },
    { key: 'LobbyistBusiness', label: 'Firm', className: 'text-muted-foreground', render: (r) => r.LobbyistBusiness || '—' },
    { key: 'Year', label: 'Year' },
    { key: 'BeginDate', label: 'Begins', className: 'whitespace-nowrap', render: (r) => formatDate(r.BeginDate) },
    { key: 'StopDate', label: 'Ends', className: 'whitespace-nowrap', render: (r) => formatDate(r.StopDate) },
    { key: 'LobbyEngagementStatus', label: 'Status', render: (r) => <StatusBadge status={r.LobbyEngagementStatus} /> },
    ...(hasCompensation(visible)
      ? [{ key: 'comp', label: 'Compensation', align: 'right', render: formatCompensation, sortValue: (r) => r.Exact ?? r.SalaryLow }]
      : []),
  ];

  const rail = (
    <Panel>
      <div className="space-y-5 p-5">
        <SectionLabel>Client</SectionLabel>
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
            <LuBuilding2 className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h1 className="font-serif text-2xl font-semibold leading-tight tracking-tight">{company.CompanyName}</h1>
            {company.ContractualName && company.ContractualName !== company.CompanyName && (
              <p className="mt-0.5 text-sm text-muted-foreground">aka {company.ContractualName}</p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-border bg-border">
          <div className="bg-card p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Lobbyists</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">{distinctLobbyists}</p>
          </div>
          <div className="bg-card p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Engagements</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">{visible.length}</p>
          </div>
        </div>
        {spend && (
          <div className="border-t border-border pt-4">
            <SectionLabel>Reported lobbying spend</SectionLabel>
            <p className="mt-1 text-lg font-semibold tabular-nums">
              {spend.low === spend.high ? money(spend.low) : `${money(spend.low)} – ${money(spend.high)}`}
            </p>
            <p className="text-xs text-muted-foreground">{activeYear === 'all' ? 'All years' : activeYear}</p>
          </div>
        )}
        {(address || company.CompanyPhone || company.CompanyEmail || company.CompanyWebsite) && (
          <div className="space-y-3 border-t border-border pt-4">
            {address && <DetailRow icon={LuMapPin} label="Address">{address}</DetailRow>}
            {company.CompanyPhone && <DetailRow icon={LuPhone} label="Phone">{company.CompanyPhone}</DetailRow>}
            {company.CompanyEmail && (
              <DetailRow icon={LuMail} label="Email">
                <a href={`mailto:${company.CompanyEmail}`} className="text-info hover:text-info-strong">
                  {company.CompanyEmail}
                </a>
              </DetailRow>
            )}
            {company.CompanyWebsite && (
              <DetailRow icon={LuGlobe} label="Website">
                <a
                  href={/^https?:\/\//i.test(company.CompanyWebsite) ? company.CompanyWebsite : `https://${company.CompanyWebsite}`}
                  target="_blank"
                  rel="noreferrer"
                  className="break-all text-info hover:text-info-strong"
                >
                  {company.CompanyWebsite}
                </a>
              </DetailRow>
            )}
          </div>
        )}
      </div>
    </Panel>
  );

  return (
    <ProfileLayout crumb={company.CompanyName} rail={rail}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-serif text-lg font-semibold">
          <LuUsers className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Lobbyist engagements
        </h2>
        <YearTabs years={years} value={activeYear} onChange={setYear} />
      </div>
      <Panel className="p-4 sm:p-5">
        <EngagementsTable
          columns={columns}
          rows={visible}
          defaultSort={{ key: 'LobbyistLastName', dir: 'asc' }}
          getRowKey={(r) => r.LobbyingEngagementID}
          emptyMessage="No lobbyists registered for this client."
        />
      </Panel>
      {(company.FullSum || company.ShortSum) && (
        <Panel className="p-5">
          <h2 className="mb-3 font-serif text-lg font-semibold">Overview</h2>
          <div
            className={`relative overflow-hidden text-sm leading-relaxed text-foreground [&_h1]:mb-1 [&_h1]:mt-4 [&_h1]:font-sans [&_h1]:text-sm [&_h1]:font-semibold [&_h2]:mb-1 [&_h2]:mt-4 [&_h2]:font-sans [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:mb-1 [&_h3]:mt-4 [&_h3]:font-sans [&_h3]:text-sm [&_h3]:font-semibold [&_h4]:mb-1 [&_h4]:mt-4 [&_h4]:font-sans [&_h4]:text-sm [&_h4]:font-semibold [&_li]:ml-5 [&_li]:list-disc [&_p]:mb-3 [&_strong]:font-semibold ${
              overviewOpen ? '' : 'max-h-48'
            }`}
          >
            <ReactMarkdown>{company.FullSum || company.ShortSum}</ReactMarkdown>
            {!overviewOpen && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-card to-transparent" />
            )}
          </div>
          <button
            type="button"
            onClick={() => setOverviewOpen((o) => !o)}
            className="mt-2 text-sm font-medium text-info hover:text-info-strong"
          >
            {overviewOpen ? 'Show less' : 'Show more'}
          </button>
        </Panel>
      )}
    </ProfileLayout>
  );
};

export default Company;
